import React, { useEffect, useRef, useState } from 'react';
import cs from 'classnames';
import { Colors } from '../../../lib/color';
import './index.scss';

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never };
// eslint-disable-next-line @typescript-eslint/ban-types
type Either<T, U> = T | U extends object ? (Without<T, U> & U) | (Without<U, T> & T) : T | U;

type AvatarProps = {
  size: number;
  src: string;
  pendantSrc?: string;
  rightBottomElement?: JSX.Element;
  className?: string;
  onClick?: (event?: React.MouseEvent) => void;
} & Either<
{
  padding?: {
    width: number;
    color: string;
  };
},
{
  margin?: {
    width: number;
    color: string;
  };
}
>;

export const Avatar = (props: AvatarProps): JSX.Element => {
  const [imageLoad, setImageLoad] = useState(false);
  const imageRef = useRef<HTMLImageElement | null>(null);

  const prefixClassName = 'design-system-avatar';
  const {
    size = 20,
    src = '',
    pendantSrc = '',
    padding = { width: 0.5, color: Colors.N100_10 },
    margin,
    rightBottomElement,
    className = '',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onClick = (): void => {},
  } = props;
  const pendantPos = (1.4 * size - size) / 2 - (margin?.width || 0);
  const iconSize = size === 96 ? 32 : size === 88 ? 30 : 0;

  const imageOnload = (): void => {
    setImageLoad(true);
  };

  useEffect(() => {
    imageRef.current?.complete && imageOnload();
  }, []);

  return (
    <div
      className={`${prefixClassName}-container ${className}`}
      style={{
        '--design-system-avatar-size': size,
        '--design-system-avatar-container-padding': margin?.width,
        backgroundColor: margin?.color,
      }}
      onClick={(event: React.MouseEvent): void => {
        onClick(event);
      }}
    >
      <div className={`${prefixClassName}-main`}>
        <div className={`${prefixClassName}-placeholder`} />
        <img
          ref={imageRef}
          className={cs(`${prefixClassName}`, {
            [`${prefixClassName}-hidden`]: !imageLoad,
          })}
          onLoad={imageOnload}
          src={src}
        />
        <div
          className={cs(`${prefixClassName}-image-mask`, {
            [`${prefixClassName}-has-border`]: padding.width && !margin?.width,
          })}
          style={{
            '--design-system-avatar-border-width': padding.width,
            '--design-system-avatar-border-color': padding.color,
          }}
        />
      </div>
      {pendantSrc && (
        <img
          className={`${prefixClassName}-pendant`}
          style={{
            '--design-system-avatar-pendant-size': size * 1.4,
            '--design-system-avatar-pendant-trans': -pendantPos,
          }}
          src={pendantSrc}
        />
      )}
      {rightBottomElement && (
        <div
          style={{
            '--design-system-avatar-icon-size': iconSize,
          }}
          className={`${prefixClassName}-right-bottom-icon`}
        >
          {rightBottomElement}
        </div>
      )}
    </div>
  );
};
